import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { hoursToSeconds, minutesToSeconds } from 'date-fns';

import { setBookmark } from 'store/bookmarks/actions';
import {
    BookmarkContentType,
    BookmarkTagsType,
} from 'store/bookmarks/reducer.types';
import { selectBookmarkByContentId } from 'store/bookmarks/selectors';
import { selectConsentMask } from 'store/config/selectors.ts';
import { StreamTypes } from 'store/player/actions.types';
import { ReduxStore } from 'store/types';
import { selectAccountId } from 'store/user/selectors';

import { VideoItem } from 'services/data-manager/types';

type Params = {
    genericVideoSessionId: string | undefined;
    videoDuration: number;
    playerReady: boolean;
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    playerProgress: number;
    recordingId?: string | undefined;
    sliderIsActive?: boolean | undefined;
};

const STREAM_TYPE_TO_BOOKMARK_CONTENT_TYPE = {
    [StreamTypes.vod]: BookmarkContentType.VOD,
    [StreamTypes.catchup]: BookmarkContentType.CATCHUP,
    [StreamTypes.npvr]: BookmarkContentType.NPVR,
};

const COMPLETION_THRESHOLD = 0.95; // Threshold for bookmark completion 95%
const MIN_WATCH_TIME = 300; // 5 minutes in seconds
const MIN_WATCH_PERCENTAGE = 0.05; // 5% of the video duration
const VOD_BOOKMARK_EXPIRY_HOURS = 48;
const COMPLETED_BOOKMARK_EXPIRY_HOURS = 72;
const BOKMARK_UPDATE_INTERVAL_MINUTES = 10;

function useBookmarks({
    genericVideoSessionId,
    videoDuration,
    playerReady,
    videoItem,
    playbackType,
    playerProgress,
    recordingId,
    sliderIsActive,
}: Params) {
    const dispatch = useDispatch();

    const [shouldCreateBookmark, setShouldCreateBookmark] = useState(false);

    const accountId = useSelector(selectAccountId);
    const bookmark = useSelector((state: ReduxStore) =>
        selectBookmarkByContentId(state, videoItem?.id),
    );
    const consentMask = useSelector(selectConsentMask);

    useEffect(() => {
        if (genericVideoSessionId) {
            setShouldCreateBookmark(
                !bookmark ||
                    !!bookmark.tags?.includes(BookmarkTagsType.COMPLETED),
            );
        }
    }, [genericVideoSessionId]);

    const isAbleToSetBookmark = useMemo(
        () =>
            !!playbackType &&
            [StreamTypes.vod, StreamTypes.npvr, StreamTypes.catchup].includes(
                playbackType,
            ),
        [playbackType],
    );

    const handleCreateBookmark = useCallback(() => {
        if (accountId && videoItem?.id) {
            const isCompleted =
                playerProgress >= videoDuration * COMPLETION_THRESHOLD;
            const vodExpires =
                !isCompleted && playbackType === StreamTypes.vod
                    ? hoursToSeconds(VOD_BOOKMARK_EXPIRY_HOURS)
                    : undefined;
            const genericExpires = isCompleted
                ? hoursToSeconds(COMPLETED_BOOKMARK_EXPIRY_HOURS)
                : undefined;

            dispatch(
                setBookmark({
                    account: accountId,
                    position: playerProgress,
                    contentId: videoItem.id,
                    contentType:
                        STREAM_TYPE_TO_BOOKMARK_CONTENT_TYPE[
                            playbackType as keyof typeof STREAM_TYPE_TO_BOOKMARK_CONTENT_TYPE
                        ],
                    tags: isCompleted
                        ? [BookmarkTagsType.COMPLETED]
                        : [BookmarkTagsType.IN_PROGRESS],
                    expires: vodExpires || genericExpires || undefined,
                    metadata: {
                        consentMask,
                        ...(recordingId && { recordingId }),
                    },
                }),
            );
        }
    }, [
        accountId,
        dispatch,
        playbackType,
        playerProgress,
        recordingId,
        videoDuration,
        videoItem?.id,
        consentMask,
    ]);

    const handleUpdateBookmarkByInterval = useCallback(
        (setToCompleted?: boolean) => {
            if (accountId && videoItem?.id) {
                const prevCompleted = bookmark?.tags?.includes(
                    BookmarkTagsType.COMPLETED,
                );
                const isCompleted =
                    setToCompleted ||
                    playerProgress >= videoDuration * COMPLETION_THRESHOLD;

                if (!(prevCompleted && isCompleted)) {
                    dispatch(
                        setBookmark({
                            account: accountId,
                            position: playerProgress,
                            contentId: videoItem.id,
                            contentType:
                                STREAM_TYPE_TO_BOOKMARK_CONTENT_TYPE[
                                    playbackType as keyof typeof STREAM_TYPE_TO_BOOKMARK_CONTENT_TYPE
                                ],
                            tags: isCompleted
                                ? [BookmarkTagsType.COMPLETED]
                                : [BookmarkTagsType.IN_PROGRESS],
                            expires: isCompleted
                                ? hoursToSeconds(
                                      COMPLETED_BOOKMARK_EXPIRY_HOURS,
                                  )
                                : undefined,
                            metadata: {
                                consentMask,
                                ...(recordingId && { recordingId }),
                            },
                        }),
                    );
                }
            }
        },
        [
            accountId,
            videoItem?.id,
            bookmark?.tags,
            playerProgress,
            videoDuration,
            dispatch,
            playbackType,
            recordingId,
            consentMask,
        ],
    );

    const handleUpdateBookmarkOnPurpose = useCallback(
        (setToCompleted?: boolean) => {
            if (
                isAbleToSetBookmark &&
                accountId &&
                videoItem?.id &&
                !shouldCreateBookmark &&
                bookmark
            ) {
                handleUpdateBookmarkByInterval(setToCompleted);
            }
        },
        [
            isAbleToSetBookmark,
            accountId,
            videoItem?.id,
            shouldCreateBookmark,
            bookmark,
            handleUpdateBookmarkByInterval,
        ],
    );

    useEffect(() => {
        if (
            playerReady &&
            accountId &&
            isAbleToSetBookmark &&
            !sliderIsActive
        ) {
            const minimumWatchRequirement =
                videoDuration * MIN_WATCH_PERCENTAGE;

            if (
                shouldCreateBookmark &&
                playerProgress >= MIN_WATCH_TIME &&
                playerProgress >= minimumWatchRequirement
            ) {
                handleCreateBookmark();
                setShouldCreateBookmark(false);
            } else if (
                !shouldCreateBookmark &&
                bookmark &&
                Math.abs(bookmark.position - playerProgress) >=
                    minutesToSeconds(BOKMARK_UPDATE_INTERVAL_MINUTES)
            ) {
                handleUpdateBookmarkByInterval();
            }
        }
    }, [
        playerReady,
        playerProgress,
        isAbleToSetBookmark,
        videoDuration,
        bookmark,
        sliderIsActive,
        accountId,
        videoItem?.id,
        dispatch,
        playbackType,
        recordingId,
        handleCreateBookmark,
        handleUpdateBookmarkByInterval,
    ]);

    return {
        onUpdateBookmark: handleUpdateBookmarkOnPurpose,
    };
}

export default useBookmarks;
